import * as React from "react";
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";

import DashboardPage from "./DashboardPage/DashboardPage";
import ErrorPage from "../public/ErrorPage/ErrorPage";
import CPAppLoader from "../../shared/components/shared/CPAppLoader/CPAppLoader";
import CPLoggedInLayout from "shared/components/app/CPLoggedInLayout/CPLoggedInLayout";
import { useCurrentUserQuery } from "data/user/user";
import AdminPage from "./AdminPage/AdminPage";
import { isTestSite } from "shared/config";
import {
  getAppState,
  updateAppState,
  clearAppState,
} from "shared/appStateStorage";
import { removeCookie } from "shared/helpers";
import http from "shared/http";
import AdminUsersPage from "./AdminUsersPage/AdminUsersPage";
import AdminPendingUsersPage from "./AdminPendingUsersPage/AdminPendingUsersPage";
import { AdminReportsPage } from "./AdminReportsPage/AdminReportsPage";
import { AdminRolesPage } from "./AdminRolesPage/AdminRolesPage";
import { AdminLinksPage } from "./AdminLinksPage/AdminLinksPage";
import { AdminDownloadsPage } from "./AdminDownloadsPage/AdminDownloadsPage";
import { AdminTiersPage } from "./AdminTiersPage/AdminTiersPage";
import { AdminPagesPage } from "./AdminPagesPage/AdminPagesPage";
import ReportPage from "./ReportPage/ReportPage";
import CategoryResourcePage from "./CategoryResourcePage/CategoryResourcePage";
import SearchDriverSettlementsPage from "./SearchDriverSettlementsPage/SearchDriverSettlementsPage";
import SearchAgentSettlementsPage from "./SearchAgentSettlementsPage/SearchAgentSettlementsPage";
import AdminAuditLogPage from "./AdminAuditLogPage/AdminAuditLogPage";
import SiteSearchPage from "./SiteSearchPage/SiteSearchPage";
import AgencyManagerUsersPage from "./AgencyManagerUsersPage/AgencyManagerUsersPage";
import AgencyManagerPendingUsersPage from "./AgencyManagerPendingUsersPage/AgencyManagerPendingUsersPage";
import AdminTestsPage from "./AdminTestsPage/AdminTestsPage";
import MyProfilePage from "./MyProfilePage/MyProfilePage";

const CPAdminLayout = React.lazy(
  () => import("shared/components/app/CPAdminLayout/CPAdminLayout"),
);

const LogoutRoute: React.FC = () => {
  const navigate = useNavigate();

  React.useEffect(() => {
    let isMounted = true;

    const doLogout = async () => {
      try {
        await http.postAsync("auth/logout", {});
      } catch {
        // best-effort server logout
      }

      const cookieName = `${window.location.host.toUpperCase()}_PPAccessToken`;
      removeCookie(cookieName);

      await clearAppState();

      if (isMounted) {
        navigate("/");
      }
    };

    doLogout();

    return () => {
      isMounted = false;
    };
  }, [navigate]);

  return null;
};

const ProtectedRoutes: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { data: user, isLoading: userLoading } = useCurrentUserQuery();
  const isAdmin =
    user?.roles.some((x: string) => x === "Admin" || x === "Site Manager") ??
    false;
  const isPureAdmin = user?.roles.some((x: string) => x === "Admin") ?? false;
  const isAgencyManager =
    (user?.roles.includes("Agency Manager") &&
      !user?.roles.some(
        (x: string) =>
          x === "Admin" || x === "Site Manager" || x === "Employee",
      )) ??
    false;
  const [useAdminLayout, setUseAdminLayout] = React.useState(false);
  const [appStateLoaded, setAppStateLoaded] = React.useState(false);

  // Remembers the last non-admin route so we can return to it when leaving admin mode.
  const lastRegularRoute = React.useRef<string>("/");

  React.useEffect(() => {
    getAppState().then((s) => {
      const isAdminPath = window.location.pathname
        .toLowerCase()
        .startsWith("/admin");

      if (s.adminLayoutEnabled !== undefined) {
        // If the URL is already an admin path, force admin mode on even if it wasn't persisted.
        const shouldUseAdmin = s.adminLayoutEnabled || isAdminPath;
        setUseAdminLayout(shouldUseAdmin);
        if (isAdminPath && !s.adminLayoutEnabled) {
          updateAppState({ adminLayoutEnabled: true });
        }
        // On initial load: if admin mode was persisted and we're sitting at /,
        // restore the last admin page visited, or fall back to /admin.
        if (s.adminLayoutEnabled === true && window.location.pathname === "/") {
          const destination = s.lastVisitedPage
            ?.toLowerCase()
            .startsWith("/admin")
            ? s.lastVisitedPage
            : "/admin";
          navigate(destination, { replace: true });
        }
      } else if (isAdminPath) {
        setUseAdminLayout(true);
        updateAppState({ adminLayoutEnabled: true });
      } else if (s.lastVisitedPage && window.location.pathname === "/") {
        // Restore last visited page on refresh when not in admin mode.
        navigate(s.lastVisitedPage, { replace: true });
      }
      setAppStateLoaded(true);
    });
  }, []);

  // Persist the current page whenever the route changes.
  React.useEffect(() => {
    if (location.pathname.toUpperCase() !== "/LOGOUT") {
      updateAppState({ lastVisitedPage: location.pathname });
    }
  }, [location.pathname]);

  const handleToggleLayout = React.useCallback(
    (value: boolean) => {
      if (value) {
        // Entering admin mode — save current route before switching.
        lastRegularRoute.current = window.location.pathname;
        setUseAdminLayout(true);
        updateAppState({ adminLayoutEnabled: true });
        navigate("/admin");
      } else {
        // Leaving admin mode — return to the route we came from.
        setUseAdminLayout(false);
        updateAppState({ adminLayoutEnabled: false });
        navigate(lastRegularRoute.current);
      }
    },
    [navigate],
  );
  
  if (!appStateLoaded || userLoading) {
    return <CPAppLoader isLoading={true} text="Evans Business Intelligence" />;
  }

  return (
    <Routes key={user?.userId}>
      <Route
        path="/"
        element={
          isAdmin && useAdminLayout ? (
            <React.Suspense
              fallback={
                <CPAppLoader
                  isLoading={true}
                  text="Evans Business Intelligence"
                />
              }
            >
              <CPAdminLayout
                isTestSite={isTestSite}
                useAdminLayout={useAdminLayout}
                onToggleLayout={handleToggleLayout}
              />
            </React.Suspense>
          ) : (
            <CPLoggedInLayout
              isTestSite={isTestSite}
              useAdminLayout={useAdminLayout}
              onToggleLayout={isAdmin ? handleToggleLayout : undefined}
            />
          )
        }
        errorElement={<ErrorPage />}
      >
        {useAdminLayout ? (
          <React.Fragment>
            <Route index element={<AdminPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/admin/users" element={<AdminUsersPage />} />
            <Route
              path="/admin/pending-users"
              element={<AdminPendingUsersPage />}
            />
            <Route path="/admin/reports" element={<AdminReportsPage />} />
            <Route path="/admin/downloads" element={<AdminDownloadsPage />} />
            <Route path="/admin/links" element={<AdminLinksPage />} />
            <Route path="/admin/roles" element={<AdminRolesPage />} />
            <Route path="/admin/tiers" element={<AdminTiersPage />} />
            <Route path="/admin/audit-log" element={<AdminAuditLogPage />} />
            <Route
              path="/admin/pages"
              element={
                isPureAdmin ? <AdminPagesPage /> : <Navigate to="/admin" />
              }
            />
            <Route
              path="/admin/tests"
              element={
                isPureAdmin ? <AdminTestsPage /> : <Navigate to="/admin" />
              }
            />
          </React.Fragment>
        ) : (
          <React.Fragment>
            <Route index element={<DashboardPage />} />
            <Route
              path="/category/:categoryName"
              element={<CategoryResourcePage />}
            />
            <Route
              path="/category/:categoryId/report/:resourceId"
              element={<ReportPage />}
            />
            <Route
              path="/driver-settlements"
              element={<SearchDriverSettlementsPage />}
            />
            <Route
              path="/agent-settlements"
              element={<SearchAgentSettlementsPage />}
            />
            <Route path="/search" element={<SiteSearchPage />} />
            
              {isAgencyManager && (
              <React.Fragment>
                {/* <Route
                  path="/agency-management/users"
                  element={<AgencyManagerUsersPage />}
                /> */}
                {/* <Route
                  path="/agency-management/pending-users"
                  element={<AgencyManagerPendingUsersPage />}
                /> */}
              </React.Fragment>
            )}
          </React.Fragment>
        )}

        <Route path="/MyProfile" element={<MyProfilePage />} />
        <Route path="/Logout" element={<LogoutRoute />} />
      </Route>
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
};
export default ProtectedRoutes;
